import { useState, useEffect } from "react";
import SEO from "./seo";
import { APP_DATA } from "./constant_data";
const QuickStart = () => {
  const [activeSection, setActiveSection] = useState("step-1");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "step-1",
        "step-2",
        "step-3",
        "step-4",
        "step-5",
        "step-6",
        "faqs",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-8">
      <SEO
        title="Quick Start Guide – Launch Your Store in Minutes | xPage"
        description="Follow this quick start guide to set up your xPage store, create your landing page, customize your design, and launch your eCommerce business in minutes."
        keywords="xPage quick start, setup guide, launch landing page, ecommerce setup, how to use xPage, online store tutorial"
        canonical="https://help.xpage.ai/help/quick-start-guide/overview"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Main Title Group */}
        <div className="mb-6">
          <h1 className="text-systemTitle text-2xl font-semibold">
            Quick Start Guide
          </h1>
          <p className="text-sm text-systemSubTitle mt-1">
            Welcome to xPage, the fastest way to launch a high-converting brand
            online. Follow this step-by-step guide to build and get your landing
            page up and running in just a few simple steps. Whether you're
            launching one or multiple landing pages, xPage makes the process
            seamless and fast.
          </p>
        </div>

        {/* Steps */}
        <div className="space-y-6">
          <div
            id="step-1"
            className="bg-systemBackground p-4 rounded-lg border border-systemBorder scroll-mt-20"
          >
            <h2 className="text-systemTitle text-lg font-semibold mb-2">
              Step 1: Complete Registration
            </h2>
            <p className="text-systemLabel text-sm">
              To begin, visit{" "}
              <a
                className="text-decoration-line: underline"
                href="https://www.xpage.ai/"
                target="_blank"
                rel="noopener noreferrer"
              >
                xPage.ai
              </a>{" "}
              and click on Start Free Trial. You'll be prompted to create your
              account by entering your email and setting a password. There's no
              credit card required, so you can get started mind free. Once
              registered, you'll be redirected straight to your personal xPage
              dashboard where the setup begins.
            </p>
          </div>

          <div
            id="step-2"
            className="bg-systemBackground p-4 rounded-lg border border-systemBorder scroll-mt-20"
          >
            <h2 className="text-systemTitle text-lg font-semibold mb-2">
              Step 2: Set Up Your Brand
            </h2>
            <p className="text-systemLabel text-sm">
              Next, it's time to define your brand identity. Start by entering
              your store name, then upload your logo or use xPage's AI-powered
              branding tool to generate one for you. You can also customize your
              brand color palette to match your style. When you're ready, enter
              a product link from platforms like AliExpress or Amazon, or
              manually input product details and import an image of the product
              along with its link, xPage will automatically generate visuals,
              branding assets, and content based on the information you provide.
            </p>
          </div>

          <div
            id="step-3"
            className="bg-systemBackground p-4 rounded-lg border border-systemBorder scroll-mt-20"
          >
            <h2 className="text-systemTitle text-lg font-semibold mb-2">
              Step 3: Generate Your Landing Page
            </h2>
            <p className="text-systemLabel text-sm">
              Once your product and brand details are set, you'll move to
              building your landing page which are perfect for single-product
              offers. xPage instantly builds a high-converting layout with key
              sections like product showcases, testimonials, guarantees, and
              mobile-first-optimized checkout flows all pre-designed.
            </p>
          </div>

          <div
            id="step-4"
            className="bg-systemBackground p-4 rounded-lg border border-systemBorder scroll-mt-20"
          >
            <h2 className="text-systemTitle text-lg font-semibold mb-2">
              Step 4: Customize with the Builder
            </h2>
            <p className="text-systemLabel text-sm">
              With your landing page structure in place, it's time to
              personalize your layout. You can adjust text, swap design
              elements, edit fonts and colors, and add powerful conversion tools
              such as upsell bundles.
            </p>
          </div>

          <div
            id="step-5"
            className="bg-systemBackground p-4 rounded-lg border border-systemBorder scroll-mt-20"
          >
            <h2 className="text-systemTitle text-lg font-semibold mb-2">
              Step 5: Final Setup and Launch
            </h2>
            <p className="text-systemLabel text-sm">
              Before you publish your landing page, you'll complete a few
              essential settings. First, connect your custom domain or use a
              free xPage subdomain, DNS setup instructions are provided inside
              your dashboard. Next, connect your preferred payment method by
              setting up Stripe, PayPal, or enabling Cash on Delivery (COD), and
              customize your checkout fields to match your region or product
              type. Finally, configure your shipping zones, estimated delivery
              timelines, and modify legal pages like your return policy, privacy
              policy, and terms of service.
            </p>
          </div>

          <div
            id="step-6"
            className="bg-systemBackground p-4 rounded-lg border border-systemBorder scroll-mt-20"
          >
            <h2 className="text-systemTitle text-lg font-semibold mb-2">
              Step 6: Manage Your Store from the Admin Panel
            </h2>
            <p className="text-systemLabel text-sm">
              After publishing, everything is managed from your xPage admin
              panel. You can track real-time sales and conversion data, view
              abandoned carts, monitor customer behavior, and create marketing
              campaigns such as discount codes and upsell offers. Fulfillment,
              shipping, and customer management are all handled in one place
              which gives you full control and visibility as your store grows.
            </p>
          </div>
        </div>

        {/* FAQs */}
        <div
          id="faqs"
          className="bg-systemBackground p-4 rounded-xl mt-6 border border-systemBorder scroll-mt-20"
        >
          <h2 className="text-systemTitle text-lg font-semibold mb-3">FAQs</h2>

          <div className="space-y-3">
            <div>
              <h3 className="text-systemTitle text-sm font-semibold">
                What is xPage?
              </h3>
              <p className="text-systemLabel text-sm">
                xPage is an AI-powered platform that lets you build and launch
                your landing pages instantly—without coding, design, or plugins.
              </p>
            </div>

            <div>
              <h3 className="text-systemTitle text-sm font-semibold">
                How long does it take to launch?
              </h3>
              <p className="text-systemLabel text-sm">
                Just a few minutes. From brand setup to publishing, everything
                is automated.
              </p>
            </div>

            <div>
              <h3 className="text-systemTitle text-sm font-semibold">
                Do I need to pay to start?
              </h3>
              <p className="text-systemLabel text-sm">
                No. You can start for free with a {APP_DATA.FREE_TRIAL_DAYS}-day
                trial. Only add payment details when you're ready to go live.
              </p>
            </div>

            <div>
              <h3 className="text-systemTitle text-sm font-semibold">
                Can I use my own domain?
              </h3>
              <p className="text-systemLabel text-sm">
                Yes, you can connect a custom domain or use a free xPage
                subdomain.
              </p>
            </div>

            <div>
              <h3 className="text-systemTitle text-sm font-semibold">
                What payment gateways are supported?
              </h3>
              <p className="text-systemLabel text-sm">
                Stripe, PayPal, Apple Pay, Klarna, and Cash on Delivery (COD)
                are supported.
              </p>
            </div>

            <div>
              <h3 className="text-systemTitle text-sm font-semibold">
                Is there support available?
              </h3>
              <p className="text-systemLabel text-sm">
                Yes! Our team offers 24/7 live support and in-app guidance. You
                also get access to our Help Center and Community.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Table of Contents Sidebar - Fixed on right */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#step-1"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "step-1"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Step 1: Registration
              </a>
            </li>
            <li>
              <a
                href="#step-2"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "step-2"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Step 2: Brand Setup
              </a>
            </li>
            <li>
              <a
                href="#step-3"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "step-3"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Step 3: Generate Page
              </a>
            </li>
            <li>
              <a
                href="#step-4"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "step-4"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Step 4: Customize
              </a>
            </li>
            <li>
              <a
                href="#step-5"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "step-5"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Step 5: Final Setup
              </a>
            </li>
            <li>
              <a
                href="#step-6"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "step-6"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Step 6: Manage Store
              </a>
            </li>
            <li>
              <a
                href="#faqs"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "faqs"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                FAQs
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default QuickStart;
